import Bacon from 'baconjs';

/**
 * A class that acts as a single broadcaster of events by name and their
 * respective payloads, in the form of a [Flux-style Dispatcher][fd].
 *
 * The Dispatcher is the basic backbone of application state, not too
 * dissimilar from an event bus, where actions are identified by name (a plain
 * old string) and the relevant payloads of those actions are broadcast to
 * receivers via [reactive streams][rs].
 *
 * For example, the adding of index list filters involves someone pushing that
 * state change to the Dispatcher, and someone else subscribing to that
 * state change:
 *
 * ```javascript
 * // someone, somewhere pushing a state change of new applied filters
 * const filters = []; // collection of filter data
 *                     // think of it as "input" to our application state
 * dispatcher.push('FILTERS-ADD', filters);
 *
 * // someone, somewhere subscribing to the stream of state changes for the add
 * // filters action
 * dispatcher.stream('FILTERS-ADD')
 *     .onValue((filters) => {
 *         // Do something with that dispatched data, filters
 *         // Think of it as "output" of our application state
 *     });
 * ```
 *
 * A singleton instance of Dispatcher is created within the application and
 * injected into the various components, downward the dependency tree. This
 * leaves it as the central source of truth for pushing state changes across
 * the application and reacting to them.
 *
 * [fd]: https://facebook.github.io/flux/docs/dispatcher.html
 * [rs]: https://baconjs.github.io/api.html#eventstream
 */
export class Dispatcher {
    constructor() {
        this.cache = {};
        this.stream = this.stream.bind(this);
        this.push = this.push.bind(this);
        this.plug = this.plug.bind(this);
        this.end = this.end.bind(this);
    }

    /**
     * @param  {string} name - Name of the stream you want to retrieve
     * @return {Bacon.Bus} - A stream associated with the given name. The stream is
     * cached, and any subsequent requests to that dispatcher for the same name
     * will return the same stream.
     */
    stream(name) {
        return this.cache[name] || (this.cache[name] = new Bacon.Bus());
    }

    push(streamName, value) {
        this.stream(streamName).push(value);
    }

    plug(streamName, sourceStream) {
        this.stream(streamName).plug(sourceStream);
    }

    end(streamName) {
        this.stream(streamName).end();
    }
}
